import { useFileUpload } from "@msuite/ui/file-upload";
import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal } from "@msuite/ui/modal";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { type ServiceFormSchema, serviceFormSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { useUpload } from "@/api/files";
import { ServiceForm } from "@/forms/service-form";
import { client } from "@/hono";
import { ModalLoadFailed } from "@/components/modals";

/** Props Interface */
interface AddServiceModalProps {
	isOpen: boolean;
	onClose: () => void;
	onComplete: () => Promise<void>;
	defaultValues?: Partial<ServiceFormSchema>;
}

export function AddServiceModal({
	isOpen,
	onClose,
	onComplete,
	defaultValues,
}: AddServiceModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { uploadFn } = useUpload();
	const fileUploadController = useFileUpload({ uploadFn });
	const { defaultValues: guardedDefaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const def = {
			unit: "piece",
			fulfillmentType: "internal",
			...(defaultValues ?? {}),
		};
		const response = await client.organizations.services["suggest-number"].$get({
			query: { parentServiceId: defaultValues?.parentServiceId || undefined },
		});
		const data = await responseHandler(response);

		if (data.suggestedNumber && !defaultValues?.number) {
			def.number = data.suggestedNumber;
		}

		return def as ServiceFormSchema;
	});
	const methods = useForm({
		resolver: resolver(serviceFormSchema),
		defaultValues: guardedDefaultValues,
	});

	/** Functions */
	async function handleOnSubmit(json: ServiceFormSchema) {
		try {
			const uploadResults = await fileUploadController.handleOnFileUpload();
			const response = await client.organizations.service.$post({
				json: {
					...json,
					uploadResults,
				},
			});
			const data = await responseHandler(response);
			if (!data.service) throw new Error("No service returned");
			await onComplete();
			methods.reset();
			onClose();
		} catch (error) {
			console.error(error);
			toast.success(t("general.something_went_wrong"));
		}
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isLoading={methods.formState.isLoading && !loadFailed}
				title={t("organization.data.add_service")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				width="60rem"
				data-cy="add-service-modal"
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<ServiceForm fileUploadController={fileUploadController} />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
